<template>
  <div class="sol-page">
    <section class="sol-hero" aria-labelledby="sol-hero-title">
      <LazyBg class="sol-hero-bg" :src="heroBg" eager />
      <div class="sol-hero-overlay" aria-hidden="true" />
      <div class="sol-hero-inner container">
        <h1 id="sol-hero-title" class="sol-hero-title">海曦智算硬件</h1>
        <p class="sol-hero-sub">为大模型与国产化场景而生</p>
      </div>
    </section>

    <section class="sol-main">
      <div class="sol-split container">
        <aside v-if="navTree.types.length" class="sol-side-nav" aria-label="产品分类导航">
          <ul class="sol-side-nav-list">
            <li v-for="(typeItem, i) in navTree.types" :key="typeItem.id" class="sol-side-group">
              <button type="button" class="sol-side-type" :class="{ 'is-expanded': isTypeExpanded(i) }"
                :aria-expanded="isTypeExpanded(i)" :id="`sol-type-tab-${typeItem.id}`" @click="toggleType(i)">
                <span class="sol-side-label">{{ typeItem.name }}</span>
                <span
                  class="sol-side-icon sol-side-icon--type"
                  :class="{ 'is-expanded': isTypeExpanded(i) }"
                  aria-hidden="true"
                >›</span>
              </button>

              <div class="sol-side-sublist-wrap" :class="{ 'is-open': isTypeExpanded(i) }"
                :aria-hidden="!isTypeExpanded(i)">
                <ul class="sol-side-sublist" role="tablist" :aria-labelledby="`sol-type-tab-${typeItem.id}`">
                  <li v-for="(cat, j) in typeItem.categories" :key="cat.id" role="presentation">
                    <button type="button" role="tab" class="sol-side-cat"
                      :class="{ 'is-active': isCategoryActive(i, j) }" :aria-selected="isCategoryActive(i, j)"
                      :aria-controls="`sol-panel-${cat.id}`" :id="`sol-cat-tab-${cat.id}`"
                      :tabindex="isTypeExpanded(i) ? 0 : -1" @click="selectCategory(i, j)">
                      <span class="sol-side-label">{{ cat.name }}</span>
                      <span class="sol-side-icon" aria-hidden="true">›</span>
                    </button>
                  </li>
                </ul>
              </div>
            </li>
          </ul>
        </aside>

        <div class="sol-content-wrap">
          <Transition name="career-panel" mode="out-in">
            <div v-if="activeCategory" :id="`sol-panel-${activeCategory.id}`"
              :key="`${activeType?.id}-${activeCategory.id}`" class="sol-panel" role="tabpanel"
              :aria-labelledby="`sol-cat-tab-${activeCategory.id}`">
              <header v-if="activeType" class="sol-panel-head">
                <p class="sol-panel-breadcrumb">
                  {{ activeType.name }}
                  <span aria-hidden="true"> / </span>
                  {{ activeCategory.name }}
                </p>
              </header>

              <p v-if="!activeCategory.products.length" class="sol-empty">该分类下暂无产品</p>

              <article v-for="p in activeCategory.products" :key="p.id" class="sol-card">
                <div class="sol-card-main">
                  <div class="sol-card-media">
                    <img :src="p.image" :alt="p.title" loading="lazy" width="320" height="200" />
                  </div>
                  <div class="sol-card-info">
                    <h2 class="sol-card-title">{{ p.title }}</h2>
                    <ul class="sol-card-meta">
                      <li class="sol-meta-row">
                        <span class="sol-meta-icon" aria-hidden="true" />
                        <div class="sol-meta-body">
                          <span class="sol-meta-text">{{ p.advantage }}</span>
                        </div>
                      </li>
                      <li class="sol-meta-row" v-if="p.scenario">
                        <span class="sol-meta-icon" aria-hidden="true" />
                        <div class="sol-meta-body">
                          <span class="sol-meta-text">{{ p.scenario }}</span>
                        </div>
                      </li>
                      <li class="sol-meta-row" v-if="p.capability">
                        <span class="sol-meta-icon" aria-hidden="true" />
                        <div class="sol-meta-body">
                          <span class="sol-meta-text">{{ p.capability }}</span>
                        </div>
                      </li>
                    </ul>

                  </div>


                </div>
                <div class="sol-card-actions">
                  <button type="button" class="sol-btn sol-btn-ghost" :aria-expanded="!!openDetails[p.id]"
                    :aria-controls="`sol-detail-${p.id}`" :disabled="!!detailLoading[p.id]"
                    @click="toggleDetail(p)">
                    查看详情&nbsp;
                    <span
                      class="sol-detail-chevron"
                      :class="{ 'is-open': openDetails[p.id] }"
                      aria-hidden="true"
                    >›</span>
                  </button>
                  <button type="button" class="sol-btn sol-btn-primary" @click="openConsult(p.phone)">

                    咨询采购 &nbsp; {{ p.phone }}
                  </button>
                </div>
                <div class="sol-expand-grid" :data-open="openDetails[p.id] ? '1' : '0'">
                  <div class="sol-expand-measure">
                    <div :id="`sol-detail-${p.id}`" class="sol-card-detail" :aria-hidden="!openDetails[p.id]">
                      <div v-if="openDetails[p.id]" class="sol-detail-body">
                        <p v-if="detailLoading[p.id]" class="sol-detail-state">加载中…</p>
                        <template v-else-if="detailErrors[p.id]">
                          <p class="sol-detail-state sol-detail-state--err">{{ detailErrors[p.id] }}</p>
                          <p v-for="(line, idx) in p.detailLines" :key="`fb-${idx}`">{{ line }}</p>
                        </template>
                        <template v-else-if="productDetails[p.id]">
                          <p v-if="productDetails[p.id]?.intro" class="sol-detail-intro">
                            {{ productDetails[p.id]?.intro }}
                          </p>
                       
                          <ul v-if="detailSpecsFor(p.id).length" class="sol-spec-list" role="list">
                            <li
                              v-for="(spec, idx) in detailSpecsFor(p.id)"
                              :key="idx"
                              class="sol-spec-row"
                            >
                              <span class="sol-spec-key">{{ spec.specKey }}</span>
                              <span class="sol-spec-value">{{ spec.specValue }}</span>
                            </li>
                          </ul>
                          <div
                            v-if="detailImagesFor(p.id).length"
                            class="sol-detail-images"
                          >
                            <img
                              v-for="(url, idx) in detailImagesFor(p.id)"
                              :key="idx"
                              :src="url"
                              :alt="`${p.title} 详情图 ${idx + 1}`"
                              loading="lazy"
                            />
                          </div>
                          <p
                            v-if="!productDetails[p.id]?.intro && !detailImagesFor(p.id).length && !detailSpecsFor(p.id).length"
                            class="sol-detail-state"
                          >
                            暂无详情内容
                          </p>
                        </template>
                        <p v-else class="sol-detail-state">暂无详情</p>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </Transition>

          <p v-if="!navTree.types.length" class="sol-empty sol-empty--page">暂无产品数据</p>
        </div>
      </div>
    </section>

    <Teleport to="body">
      <div v-if="consultOpen" class="sol-modal-root" role="presentation">
        <div class="sol-modal-backdrop" aria-hidden="true" @click="consultOpen = false" />
        <div class="sol-modal" role="dialog" aria-modal="true" aria-labelledby="sol-modal-title" tabindex="-1">
          <h2 id="sol-modal-title" class="sol-modal-title">咨询采购</h2>
          <p class="sol-modal-desc">商务热线（工作日 9:30–18:30）</p>
          <a class="sol-modal-tel" :href="`tel:${consultTelDigits}`">{{ consultTelDisplay }}</a>
          <button type="button" class="sol-modal-close" @click="consultOpen = false">关闭</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import LazyBg from '@/components/LazyBg.vue';
import heroBg from '@/assets/cpzx.png';
import { fetchFrontProductDetail } from '@/api/front';
import type { FrontProductDetail } from '@/api/types/cms';
import { useSeo } from '@/composables/useSeo';
import { useFrontCms } from '@/composables/useFrontCms';
import {
  mapProductCenterToNav,
  type ProductCardView,
  type ProductCenterNavView
} from '@/utils/frontCmsMappers';

const { productCenter } = useFrontCms('productCenter');

const CONSULT_TEL = '400-001-5020';
const CONSULT_TEL_DIGITS = '4000015020';

const IMG_TRAIN =
  'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=640&q=80';
const IMG_INFER =
  'https://images.unsplash.com/photo-159785207-7b7a1d2f4b58?auto=format&fit=crop&w=640&q=80';
const IMG_EDGE =
  'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=640&q=80';
const IMG_AIO =
  'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=640&q=80';

const TRAIN_CARD: ProductCardView = {
  id: 'train',
  title: '海曦智算训练服务器',
  image: IMG_TRAIN,
  advantage: '多卡高速互联与散热优化，支持大规模分布式训练稳定长跑。',
  scenario: '大模型预训练、行业模型微调、科研算力池与高吞吐数据预处理。',
  capability: '兼容主流国产加速卡与软件栈，提供集群调度、监控与故障自愈能力。',
  detailLines: [
    '整机通过软硬件联合调优，在典型 Transformer 训练任务上实现更高 GPU 利用率与更短迭代周期。',
    '支持液冷与风冷多种机房条件，提供上架规划、压测与性能调优等交付服务，缩短客户上线时间。',
    '可选安全加固与审计套件，满足政企与关键行业对算力平台的合规要求。'
  ]
};

const INFER_CARD: ProductCardView = {
  id: 'infer',
  title: '海曦智算推理服务器',
  image: IMG_INFER,
  advantage: '面向高并发推理优化，兼顾时延与吞吐，支持多模型并行部署与弹性扩缩。',
  scenario: '在线问答、检索增强、实时风控、音视频理解与边缘机房汇聚推理。',
  capability: '内置推理服务框架与灰度发布能力，支持 INT8/FP16 等混合精度策略。',
  detailLines: [
    '提供模型版本管理与 A/B 实验能力，便于在不影响业务的前提下平滑升级模型。',
    '与国产操作系统、容器平台深度适配，支持一键扩容与健康检查，降低运维复杂度。',
    '可选同城双活与只读副本方案，提升关键业务在峰值流量下的可用性。'
  ]
};

const EDGE_CARD: ProductCardView = {
  id: 'edge',
  title: '边缘计算智能终端',
  image: IMG_EDGE,
  advantage: '小型化、低功耗、强算力，适配现场恶劣环境与弱网条件。',
  scenario: '工业质检、园区安防、智慧教室、医疗终端与移动巡检等边缘场景。',
  capability: '端侧模型热更新、离线缓存与数据回传加密，保障业务连续与数据安全。',
  detailLines: [
    '设备支持宽温宽压与工业级接口，便于与 PLC、摄像头及传感器快速对接。',
    '内置轻量化多模态推理引擎，可在本地完成实时识别与告警，减少回传带宽压力。',
    '提供远程运维与批量升级工具，支持万台级终端的统一策略下发与状态监控。'
  ]
};

const FALLBACK_NAV: ProductCenterNavView = {
  types: [
    {
      id: 'hw',
      name: '智算硬件系列',
      categories: [
        { id: 'rack', name: '机架服务器', products: [TRAIN_CARD, INFER_CARD] },
        { id: 'edge', name: '边缘终端', products: [EDGE_CARD] }
      ]
    },
    {
      id: 'aio',
      name: 'AI 一体机系列',
      categories: [
        {
          id: 'aio-all',
          name: '一体机',
          products: [
            {
              id: 'aio-std',
              title: '海曦 AI 训推一体机（标准版）',
              image: IMG_AIO,
              advantage: '软硬件预集成，开箱即用，内置多模态平台与常用行业模板，降低 AI 落地门槛。',
              scenario: '地市政务云、企业私有化智算中心、院校实训室与中型研发团队。',
              capability: '支持可视化编排、权限分级与资源配额管理，可按项目快速交付与扩容。',
              detailLines: [
                '出厂完成兼容性验证与安全基线配置，客户侧仅需少量参数即可接入现有业务系统。',
                '内置监控大屏与告警策略，覆盖 GPU、存储、服务与模型调用链路，便于 IT 统一运维。',
                '支持与海曦云端模型市场同步，按需拉取或更新行业模型资产。'
              ]
            },
            {
              id: 'aio-compact',
              title: '海曦 AI 训推一体机（紧凑版）',
              image: IMG_INFER,
              advantage: '单机柜即可交付，适合分支机构、边缘机房或临时活动场景的快速部署。',
              scenario: '分公司智能客服、展会演示、应急指挥与小型医疗机构辅助诊断试点。',
              capability: '保留核心训推能力的同时优化功耗与噪声，支持机架式与塔式两种形态。',
              detailLines: [
                '紧凑版在体积与噪音控制上进一步优化，适合办公区或弱机房环境就近部署。',
                '同样支持国产化软硬件栈与等保加固选项，满足中小型客户对安全可控的诉求。',
                '可与标准版组成「中心训练 + 边缘推理」分层架构，实现模型统一管理与就近推理。'
              ]
            }
          ]
        }
      ]
    }
  ]
};

const navTree = computed(() => {
  const fromApi = mapProductCenterToNav(productCenter.value);
  return fromApi.types.length ? fromApi : FALLBACK_NAV;
});

const activeTypeIndex = ref(0);
const activeCategoryIndex = ref(0);
const expandedTypeIndex = ref<number | null>(0);
const openDetails = reactive<Record<string, boolean>>({});
const productDetails = reactive<Record<string, FrontProductDetail>>({});
const detailLoading = reactive<Record<string, boolean>>({});
const detailErrors = reactive<Record<string, string>>({});
const consultOpen = ref(false);
const consultTelDisplay = ref(CONSULT_TEL);
const consultTelDigits = ref(CONSULT_TEL_DIGITS);

const activeType = computed(() => navTree.value.types[activeTypeIndex.value] ?? null);

const activeCategory = computed(() => {
  const type = activeType.value;
  if (!type?.categories.length) {
    return null;
  }
  const idx = Math.min(activeCategoryIndex.value, type.categories.length - 1);
  return type.categories[idx] ?? null;
});

const clearOpenDetails = () => {
  for (const key of Object.keys(openDetails)) {
    openDetails[key] = false;
  }
};

const isTypeExpanded = (index: number) => expandedTypeIndex.value === index;

const isCategoryActive = (typeIndex: number, categoryIndex: number) =>
  activeTypeIndex.value === typeIndex && activeCategoryIndex.value === categoryIndex;

const toggleType = (index: number) => {
  if (expandedTypeIndex.value === index) {
    expandedTypeIndex.value = null;
    clearOpenDetails();
    return;
  }
  expandedTypeIndex.value = index;
  activeTypeIndex.value = index;
  activeCategoryIndex.value = 0;
  clearOpenDetails();
};

const selectCategory = (typeIndex: number, categoryIndex: number) => {
  expandedTypeIndex.value = typeIndex;
  activeTypeIndex.value = typeIndex;
  activeCategoryIndex.value = categoryIndex;
  clearOpenDetails();
};

function detailImagesFor(id: string): string[] {
  return (productDetails[id]?.detailImageUrls ?? [])
    .map((u) => u?.trim())
    .filter((u): u is string => Boolean(u));
}

function detailSpecsFor(id: string) {
  return (productDetails[id]?.specs ?? []).filter((s) => s.specKey?.trim() || s.specValue?.trim());
}

async function toggleDetail(card: ProductCardView) {
  const id = card.id;
  const willOpen = !openDetails[id];
  openDetails[id] = willOpen;
  if (!willOpen || productDetails[id] || detailLoading[id]) return;

  detailLoading[id] = true;
  detailErrors[id] = '';
  try {
    productDetails[id] = await fetchFrontProductDetail(id);
  } catch (e) {
    detailErrors[id] = e instanceof Error ? e.message : '加载失败';
  } finally {
    detailLoading[id] = false;
  }
}

const openConsult = (phone?: string) => {
  if (phone) {
    const digits = phone.replace(/\D/g, '');
    consultTelDisplay.value = phone;
    consultTelDigits.value = digits || CONSULT_TEL_DIGITS;
  } else {
    consultTelDisplay.value = CONSULT_TEL;
    consultTelDigits.value = CONSULT_TEL_DIGITS;
  }
  consultOpen.value = true;
};

watch(navTree, (tree) => {
  if (activeTypeIndex.value >= tree.types.length) {
    activeTypeIndex.value = 0;
  }
  if (expandedTypeIndex.value !== null && expandedTypeIndex.value >= tree.types.length) {
    expandedTypeIndex.value = 0;
  }
  const type = tree.types[activeTypeIndex.value];
  if (type && activeCategoryIndex.value >= type.categories.length) {
    activeCategoryIndex.value = 0;
  }
});

let escOff: (() => void) | null = null;
watch(consultOpen, (open) => {
  escOff?.();
  escOff = null;
  if (!open) return;
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') consultOpen.value = false;
  };
  document.addEventListener('keydown', onKey);
  escOff = () => document.removeEventListener('keydown', onKey);
});
onUnmounted(() => escOff?.());

useSeo({
  title: '产品中心',
  description: '海曦产品中心：智算硬件与 AI 一体机，覆盖训练、推理与边缘场景。'
});
</script>

<style scoped lang="scss">
.sol-page {
  color: #333333;
}

.sol-hero {
  position: relative;
  min-height: 280px;
  display: flex;
  align-items: center;
  padding: 100px 0 56px;
  overflow: hidden;
}

.sol-hero-bg {
  /* LazyBg */
}

.sol-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, rgba(5, 10, 22, 0.75) 0%, rgba(5, 10, 22, 0.45) 55%, rgba(5, 10, 22, 0.35) 100%);
}

.sol-hero-inner {
  position: relative;
  z-index: 1;
}


.sol-hero-title {
  margin: 0 0 12px;
  font-size: var(--fluid-h1, 52px);
  font-weight: 500;
  letter-spacing: 4.16px;
  color: #fff;
}

.sol-hero-sub {
  margin: 0;
  font-family: "Noto Sans SC";
  font-size: 36px;
  letter-spacing: 4.32px;
  color: #fff;

}

.sol-main {
  padding: 48px 0 80px;
  background: linear-gradient(180deg, #f0f2f5 0%, #f7f8fa 40%, #ffffff 100%);
}

.sol-split {
  display: grid;
  grid-template-columns: minmax(220px, 248px) 1fr;
  gap: 0;
  align-items: start;
}

.sol-side-nav {
  flex-shrink: 0;
  border-right: 1px solid #e8e8e8;
  background: #fff;
}

.sol-side-nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sol-side-group {
  margin: 0;
  padding: 0;
}

.sol-side-type,
.sol-side-cat {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 0;
  padding: 18px 16px;
  border: none;
  font: inherit;
  font-size: 15px;
  line-height: 1.4;
  text-align: left;
  cursor: pointer;
  transition:
    color 0.25s ease,
    background-color 0.25s ease,
    padding-left 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.sol-side-type {
  color: #333;
  background: #ececec;

  &:hover {
    background: #e4e4e4;
  }

  &.is-expanded {
    background: #ececec;
  }
}

.sol-side-sublist-wrap {
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows 0.32s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-open {
    grid-template-rows: 1fr;
  }
}

.sol-side-sublist {
  list-style: none;
  margin: 0;
  padding: 0;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  opacity: 0;
  transition: opacity 0.24s ease;

  .sol-side-sublist-wrap.is-open & {
    opacity: 1;
    transition-delay: 0.06s;
  }
}

.sol-side-cat {
  color: #4a4a4a;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;

  &:hover {
    color: #0095ff;
    background: #fafcff;
    padding-left: 20px;

    .sol-side-icon {
      color: #0095ff;
      transform: translateX(3px);
    }
  }

  &.is-active {
    color: #0095FF;
    font-weight: 500;

    .sol-side-icon {
      color: #0095FF;
    }
  }
}

.sol-side-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sol-side-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1em;
  font-size: 18px;
  line-height: 1;
  color: #9a9a9a;
  transition:
    color 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);

  &--type {
    color: #666;
    transform: rotate(0deg);
    transition:
      transform 0.32s cubic-bezier(0.4, 0, 0.2, 1),
      color 0.2s ease;

    &.is-expanded {
      transform: rotate(90deg);
    }
  }
}

.sol-panel-head {
  margin-bottom: 20px;
}

.sol-panel-breadcrumb {
  margin: 0;
  font-size: 14px;
  color: #8c8c8c;

  span {
    margin: 0 4px;
    color: #c0c0c0;
  }
}

.sol-empty {
  margin: 24px 0;
  padding: 32px;
  text-align: center;
  font-size: 15px;
  color: #8c8c8c;
  background: #fafafa;
  border: 1px dashed #e0e0e0;
  border-radius: 8px;

  &--page {
    margin-top: 48px;
  }
}

.sol-content-wrap {
  padding: 0 0 24px 40px;
  min-height: 200px;
}

.sol-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.sol-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 8px 28px rgba(15, 23, 42, 0.08);
  border: 1px solid rgba(15, 23, 42, 0.06);
  overflow: hidden;
  transition:
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 149, 255, 0.16);
    box-shadow: 0 18px 44px rgba(15, 23, 42, 0.11);

    .sol-card-media img {
      transform: scale(1.04);
    }

    .sol-card-title {
      color: #0095ff;
    }
  }
}

.sol-card-main {
  display: grid;
  grid-template-columns: minmax(200px, 300px) 1fr;
  gap: 24px;
  padding: 24px 24px 0;
  align-items: start;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.sol-card-media {
  border-radius: 10px;
  overflow: hidden;
  background: #e5e7eb;
  aspect-ratio: 16 / 10;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
  }
}

.sol-card-title {
  margin: 0 0 18px;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  transition: color 0.25s ease;
}

.sol-card-meta {
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
}

.sol-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 14px;
  font-style: normal;
  line-height: 22px;

  &:last-child {
    margin-bottom: 0;
  }
}

.sol-meta-icon {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  margin-top: 8px;
  background: #333;
  border-radius: 1px;
}

.sol-meta-body {
  min-width: 0;
}

.sol-meta-label {
  font-weight: 700;
  color: #1f2937;
  margin-right: 6px;
}

.sol-meta-text {
  color: #4b5563;
}

.sol-card-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
}

.sol-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 4px;
  color: #333;
  border: 1px solid rgba(255, 255, 255, 0.60);
  background: #F5F5F5;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  border: none;
  font-family: inherit;
  transition:
    background 0.25s ease,
    color 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.sol-btn-primary {
  color: #fff;
  background: #0095ff;
  border: 1px solid #0095ff;

  &:hover {
    background: #0084e6;
    border-color: #0084e6;
    box-shadow: 0 8px 22px rgba(0, 149, 255, 0.32);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

.prod-btn-secondary {
  &:hover {
    background: #e5e7eb;
    color: #111827;
    transform: translateY(-1px);
  }
}

.sol-btn-ghost {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #e5e7eb;

  &:hover {
    color: #0095ff;
    background: rgba(0, 149, 255, 0.06);
    border-color: rgba(0, 149, 255, 0.35);
    transform: translateY(-1px);
  }
}



.sol-phone-ico {
  width: 16px;
  height: 16px;
}

.sol-detail-chevron {
  display: inline-block;
  font-size: 16px;
  line-height: 1;
  transition: transform 0.25s ease;
  transform: rotate(0deg);
}

.sol-detail-chevron.is-open {
  transform: rotate(90deg);
}

.sol-expand-grid {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.sol-expand-grid[data-open='1'] {
  grid-template-rows: 1fr;
}

.sol-expand-measure {
  overflow: hidden;
  min-height: 0;
}

.sol-card-detail {
  padding: 0 24px 22px;
  border-top: 1px solid #f0f1f4;
  background: #fafbfc;
}

.sol-detail-body {
  padding-top: 18px;
  padding-bottom: 4px;
}

.sol-detail-state {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.75;
  color: #6b7280;

  &--err {
    color: #dc2626;
  }
}

.sol-detail-intro {
  margin: 0 0 16px;
  font-size: 14px;
  line-height: 1.75;
  color: #4b5563;
  white-space: pre-wrap;
}

.sol-detail-images {
  gap: 12px;
  margin-bottom: 16px;

  img {
    margin: 12px 0;
    display: block;
    width: 100%;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
  }
}

.sol-spec-list {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid #e8eaef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.sol-spec-row {
  display: grid;
  grid-template-columns: minmax(96px, 32%) 1fr;
  gap: 12px 20px;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.6;
  border-bottom: 1px solid #eef0f4;

  &:last-child {
    border-bottom: none;
  }
}

.sol-spec-key {
  color: #6b7280;
  font-weight: 500;
}

.sol-spec-value {
  color: #1f2937;
  word-break: break-word;
}

.sol-card-detail p {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.75;
  color: #4b5563;

  &:last-child {
    margin-bottom: 0;
  }
}

/* 与人才招聘页一致的右侧内容切换 */
.career-panel-enter-active,
.career-panel-leave-active {
  transition:
    opacity 0.28s ease,
    transform 0.28s ease;
}

.career-panel-enter-from {
  opacity: 0;
  transform: translateX(14px);
}

.career-panel-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.sol-modal-root {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.sol-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
}

.sol-modal {
  position: relative;
  width: min(400px, 100%);
  padding: 28px 24px 22px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.sol-modal-title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.sol-modal-desc {
  margin: 0 0 16px;
  font-size: 14px;
  color: #6b7280;
}

.sol-modal-tel {
  display: inline-block;
  font-size: 26px;
  font-weight: 700;
  color: #0095FF;
  text-decoration: none;
  letter-spacing: 0.04em;
  margin-bottom: 20px;

  &:hover {
    text-decoration: underline;
  }
}

.sol-modal-close {
  display: block;
  width: 100%;
  margin: 0;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: #f3f4f6;
  }
}

@media (max-width: 900px) {
  .sol-card-main {
    grid-template-columns: 1fr;
  }

  .sol-split {
    grid-template-columns: 1fr;
  }

  .sol-side-nav {
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
    margin-bottom: 8px;
  }

  .sol-content-wrap {
    padding: 16px 0 32px;
  }

  .career-panel-enter-from {
    transform: translateY(10px);
  }

  .career-panel-leave-to {
    transform: translateY(-8px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .sol-card,
  .sol-card-media img,
  .sol-card-title,
  .sol-side-type,
  .sol-side-cat,
  .sol-side-icon,
  .sol-btn {
    transition: none;
  }

  .sol-card:hover,
  .sol-btn-primary:hover,
  .sol-btn-ghost:hover,
  .prod-btn-secondary:hover {
    transform: none;
  }

  .sol-card:hover .sol-card-media img {
    transform: none;
  }

  .sol-side-cat:hover .sol-side-icon {
    transform: none;
  }
}
</style>
